import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { MenuIcons } from "../atoms/MenuIcons";
import { NavTab } from "../molecules/NavTab";
import type { NavTab as NavTabType, IconName } from "../../types";

interface BottomNavigationProps {
  className?: string;
}

type TabConfig = {
  id: NavTabType;
  label: string;
  icon: IconName;
  route: string | null;
};

const tabs: TabConfig[] = [
  { id: "home", label: "Inicio", icon: "home", route: "/inicio" },
  {
    id: "create",
    label: "Crear Oferta",
    icon: "target",
    route: "/ofertas/crear-oferta",
  },
  { id: "validate", label: "Validación", icon: "check", route: null },
  { id: "disbursement", label: "Desembolso", icon: "bag", route: null },
  { id: "tracking", label: "Seguimiento", icon: "chart", route: null },
];

const NavContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: ${(props) => props.theme.colors.primary[1000]};
  padding-top: ${(props) => props.theme.spacing(2)};
  padding-bottom: ${(props) => props.theme.spacing(0.5)};
  z-index: 1000;

  ${(props) => props.theme.breakpoints.up("tablet")} {
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    min-width: 500px;
    max-width: 600px;
    bottom: ${(props) => props.theme.spacing(2)};
    border-radius: ${(props) => props.theme.shape.borderRadiusXLarge};
    box-shadow: ${(props) => props.theme.shadows.xl};
  }
`;

const TabsWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  padding: 0 ${(props) => props.theme.spacing(2)};
  margin-bottom: ${(props) => props.theme.spacing(1.75)};
`;

const TabItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const CenterTabWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CenterCircle = styled.div<{ disabled: boolean }>`
  position: absolute;
  top: -40px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.primary[1000]};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 -4px 12px rgba(0, -10, 0, 2.15);
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const CenterLabel = styled.span<{ active: boolean; disabled: boolean }>`
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.captionBold.fontSize};
  font-weight: ${(props) =>
    props.active
      ? props.theme.typography.captionBold.fontWeight
      : props.theme.typography.captionRegular.fontWeight};
  color: ${(props) =>
    props.active
      ? props.theme.colors.secondary[1000]
      : props.theme.colors.tertiary[1000]};
  text-align: center;
  white-space: nowrap;
  margin-top: ${(props) => props.theme.spacing(3.5)};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const DisabledNavTab = styled.div`
  opacity: 0.5;
  cursor: not-allowed;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DisabledIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DisabledLabel = styled.span`
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.captionRegular.fontSize};
  font-weight: ${(props) => props.theme.typography.captionRegular.fontWeight};
  color: ${(props) => props.theme.colors.tertiary[1000]};
  text-align: center;
  white-space: nowrap;
`;

const getActiveTab = (pathname: string): NavTabType | null => {
  if (pathname.startsWith("/inicio")) return "home";
  if (pathname.startsWith("/ofertas")) return "create";
  return null;
};

export const BottomNavigation = ({ className }: BottomNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = getActiveTab(location.pathname);

  const handleTabClick = (tab: TabConfig) => {
    if (tab.route) {
      navigate(tab.route);
    }
  };

  return (
    <NavContainer className={className}>
      <TabsWrapper>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const isDisabled = tab.route === null;

          if (tab.id === "validate") {
            return (
              <TabItem key={tab.id}>
                <CenterTabWrapper>
                  <CenterCircle
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleTabClick(tab)}
                  >
                    <MenuIcons
                      name="check"
                      color={isActive ? "secondary" : "tertiary"}
                      size={32}
                    />
                  </CenterCircle>
                  <CenterLabel active={isActive} disabled={isDisabled}>
                    {tab.label}
                  </CenterLabel>
                </CenterTabWrapper>
              </TabItem>
            );
          }

          if (isDisabled) {
            return (
              <TabItem key={tab.id}>
                <DisabledNavTab>
                  <DisabledIconWrapper>
                    <MenuIcons name={tab.icon} color="tertiary" size={24} />
                  </DisabledIconWrapper>
                  <DisabledLabel>{tab.label}</DisabledLabel>
                </DisabledNavTab>
              </TabItem>
            );
          }

          return (
            <TabItem key={tab.id}>
              <NavTab
                icon={
                  <MenuIcons
                    name={tab.icon}
                    color={isActive ? "secondary" : "tertiary"}
                    size={24}
                  />
                }
                label={tab.label}
                active={isActive}
                onClick={() => handleTabClick(tab)}
              />
            </TabItem>
          );
        })}
      </TabsWrapper>
    </NavContainer>
  );
};
